import { tw } from "@careinfo/tailwind";
import {
  CalendarIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  PlusIcon,
} from "@heroicons/react/24/outline";
import { parseDate, DateValue, today } from "@internationalized/date";
import {
  Button,
  Calendar,
  CalendarCell,
  CalendarGrid,
  DatePicker,
  DateInput,
  DateSegment,
  Dialog,
  Group,
  Heading,
  Label,
  Popover,
} from "react-aria-components";
import { Control, FieldValues, Path, useController } from "react-hook-form";
import "./style.css";

interface Props<T extends FieldValues> {
  name: Path<T>;
  control: Control<T>;
  label?: string;
  placeholder?: string;
  disabled?: boolean;
  limitMaxDay?: boolean;
}

export default function FormDatePicker<T extends FieldValues>({
  name,
  control,
  disabled = false,
  limitMaxDay = true,
  label = "Date",
  placeholder = "Select date",
}: Props<T>) {
  const {
    field: { value = "", onChange },
    fieldState: { error },
  } = useController({
    name,
    control,
  });

  const formatToMMDDYYYY = (date: DateValue | null) => {
    if (!date) return "";
    const jsDate = new Date(date.toString());
    return jsDate.toLocaleDateString("en-IN", {
      month: "2-digit",
      day: "2-digit",
      year: "numeric",
    });
  };

  return (
    <DatePicker
      isDisabled={disabled}
      hideTimeZone
      value={value ? parseDate(value) : null}
      maxValue={limitMaxDay ? today("UTC") : null}
      onChange={(date) => {
        if (date) {
          const isoDate = new Date(date.toString()).toISOString().split("T")[0];
          onChange(isoDate);
        } else {
          onChange("");
        }
      }}
      className={tw`react-aria-DatePicker `}
    >
      <Label className={tw`block  text-[#272727]`}>{label}</Label>

      <Group
        className={tw`bg-[#EFF4F4] mt-2 focus:outline-none py-2 px-4 w-full rounded-sm flex items-center justify-between`}
      >
        <DateInput className={tw`flex-1 flex gap-0.5 outline-none`}>
          {(segment) => (
            <DateSegment
              segment={segment}
              className={tw`px-0.5 text-black data-placeholder:text-[#949494]`}
              aria-label={formatToMMDDYYYY(value ? parseDate(value) : null)}
            />
          )}
        </DateInput>

        {value && (
          <button
            type="button"
            className={tw`focus:outline-none rounded-full px-1 rotate-[45deg]`}
            onClick={(e) => {
              e.preventDefault();
              onChange("");
            }}
          >
            <PlusIcon
              className={tw`h-5 w-5 text-gray-400`}
              aria-hidden="true"
            />
          </button>
        )}

        {/* Calendar icon */}
        <Button className={tw`focus:outline-none`}>
          <CalendarIcon
            className={tw`h-5 w-5 text-gray-400`}
            aria-hidden="true"
          />
        </Button>
      </Group>

      {error && (
        <span
          className={tw`text-red-500 text-xs flex items-center gap-x-1 mt-2`}
        >
          <span>{error.message}</span>
        </span>
      )}

      <Popover
        className={tw`bg-white max-w-[300px] p-2 rounded-md react-aria-DatePopover`}
        style={{
          width: "var(--trigger-width)",
          boxShadow: "0px 1px 6px 0px #00000040",
        }}
      >
        <Dialog>
          <Calendar className={tw`space-y-2`}>
            <header className="flex justify-between items-center">
              <Button slot="previous" className={tw`focus:outline-none`}>
                <ChevronLeftIcon className={tw`h-5 w-5`} aria-hidden="true" />
              </Button>
              <Heading />
              <Button slot="next" className={tw`focus:outline-none`}>
                <ChevronRightIcon className={tw`h-5 w-5`} aria-hidden="true" />
              </Button>
            </header>
            <CalendarGrid className={tw`w-full text-left`}>
              {(date) => <CalendarCell date={date} />}
            </CalendarGrid>
          </Calendar>
        </Dialog>
      </Popover>
    </DatePicker>
  );
}
